import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from './en.json';
import ar from './ar.json';
import de from './de.json';
import es from './es.json';

export const LANGUAGES = ['EN', 'AR', 'DE', 'ES'];

const DICTS = {
  EN: en,
  AR: ar,
  DE: de,
  ES: es,
};

const STORAGE_KEY = 'glowmeets.lang';

const detectInitial = () => {
  if (typeof window === 'undefined') return 'EN';
  // 1) URL query takes precedence so deep-links / search-engine alternates work
  try {
    const params = new URLSearchParams(window.location.search);
    const q = (params.get('lang') || '').toUpperCase();
    if (LANGUAGES.includes(q)) return q;
  } catch (_) {
    // ignore
  }
  // 2) Persisted preference
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && LANGUAGES.includes(saved)) return saved;
  // 3) Browser locale
  const nav = (window.navigator.language || 'en').slice(0, 2).toLowerCase();
  if (nav === 'ar') return 'AR';
  if (nav === 'de') return 'DE';
  if (nav === 'es') return 'ES';
  return 'EN';
};

const LanguageContext = createContext({
  lang: 'EN',
  setLang: () => {},
  dir: 'ltr',
  t: (k) => k,
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState('EN');

  // Initialise on mount (avoids SSR hydration issues)
  useEffect(() => {
    setLangState(detectInitial());
  }, []);

  // Sync <html lang> and <html dir> + Tajawal class for AR
  useEffect(() => {
    const meta = DICTS[lang]?._meta || {};
    const htmlEl = document.documentElement;
    htmlEl.setAttribute('lang', meta.lang || 'en');
    htmlEl.setAttribute('dir', meta.dir || 'ltr');
    if (lang === 'AR') {
      document.body.classList.add('lang-ar');
    } else {
      document.body.classList.remove('lang-ar');
    }
  }, [lang]);

  const setLang = (next) => {
    if (!LANGUAGES.includes(next)) return;
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // localStorage unavailable, ignore
    }
  };

  const t = useMemo(() => {
    const dict = DICTS[lang] || en;
    const fallback = en;

    const lookup = (source, key) => {
      const parts = key.split('.');
      let node = source;
      for (const p of parts) {
        if (node && typeof node === 'object' && p in node) {
          node = node[p];
        } else {
          return undefined;
        }
      }
      return node;
    };

    return (key, options) => {
      // Walk up to find the deepest container that has _status: needs_review
      const parts = key.split('.');
      let node = dict;
      let needsReview = false;
      for (const p of parts) {
        if (node && typeof node === 'object') {
          if (node._status === 'needs_review') {
            needsReview = true;
            break;
          }
          node = node[p];
        } else {
          break;
        }
      }

      let value;
      if (needsReview) {
        value = lookup(fallback, key);
      } else {
        value = lookup(dict, key);
        if (value === undefined) value = lookup(fallback, key);
      }

      if (value === undefined) return key;

      // Simple {{var}} interpolation
      if (typeof value === 'string' && options) {
        return value.replace(/\{\{(\w+)\}\}/g, (_, k) => options[k] ?? '');
      }
      return value;
    };
  }, [lang]);

  const dir = DICTS[lang]?._meta?.dir || 'ltr';

  return (
    <LanguageContext.Provider value={{ lang, setLang, dir, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => useContext(LanguageContext);
export const useT = () => useContext(LanguageContext).t;

export default LanguageContext;
